import { Transition } from '@mantine/core'
import { useReducedMotion } from '@mantine/hooks'
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { createRainDrops, randomMilliseconds, RAIN_TIMING } from '../lib/weather'

type RainDropStyle = CSSProperties & {
  '--rain-left': string
  '--rain-length': string
  '--rain-duration': string
  '--rain-delay': string
  '--rain-opacity': string
}

/**
 * A quiet, intermittent shower over the home page. Mantine owns the soft
 * enter/exit transition; CSS keeps the individual drops inexpensive.
 */
export function RainShower() {
  const reduceMotion = useReducedMotion()
  const [raining, setRaining] = useState(false)
  const drops = useMemo(() => createRainDrops(), [])

  useEffect(() => {
    if (reduceMotion) {
      setRaining(false)
      return
    }

    let timeout: number | undefined
    let firstShower = true

    const scheduleDrySpell = () => {
      const range = firstShower ? RAIN_TIMING.initialDry : RAIN_TIMING.dry
      firstShower = false
      timeout = window.setTimeout(() => {
        setRaining(true)
        scheduleShower()
      }, randomMilliseconds(range))
    }

    const scheduleShower = () => {
      timeout = window.setTimeout(() => {
        setRaining(false)
        scheduleDrySpell()
      }, randomMilliseconds(RAIN_TIMING.wet))
    }

    scheduleDrySpell()
    return () => window.clearTimeout(timeout)
  }, [reduceMotion])

  return (
    <Transition
      mounted={raining && !reduceMotion}
      transition="fade"
      duration={900}
      exitDuration={1_250}
      timingFunction="ease"
    >
      {transitionStyle => (
        <div className="weather-rain" style={transitionStyle} aria-hidden="true" data-weather="rain">
          {drops.map(drop => {
            const style: RainDropStyle = {
              '--rain-left': `${drop.left.toFixed(2)}%`,
              '--rain-length': `${drop.length.toFixed(1)}px`,
              '--rain-duration': `${drop.duration.toFixed(2)}s`,
              '--rain-delay': `${drop.delay.toFixed(2)}s`,
              '--rain-opacity': drop.opacity.toFixed(2),
            }

            return <span className="weather-rain-drop" style={style} key={drop.id} />
          })}
        </div>
      )}
    </Transition>
  )
}
